import { createTheme } from '@mui/material/styles';

/**
 * The visual vocabulary of the interface.
 *
 * Two families, and they do not do the same job: Newsreader carries the
 * headings, Karla everything that is read rather than glanced at. Karla is
 * kept for body copy on purpose — a serif at 15px is measurably slower to
 * read, and a good part of the audience is not a fluent screen reader.
 *
 * The fonts are declared in `public/index.html`; both stacks name a fallback
 * with close metrics so a blocked request degrades rather than breaks.
 */
const HEADING_FONT = "'Newsreader', Georgia, 'Times New Roman', serif";
const BODY_FONT = "'Karla', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif";

/**
 * Paper, terracotta and olive.
 *
 * Terracotta is the colour of *doing something* — it is spent only on the
 * primary action of a screen. Olive is the community: the feed, the
 * neighbourhood, anything the residents make between them. Nothing else gets
 * a saturated colour, which is what stops the interface shouting in five
 * directions at once.
 */
const PAPER = '#faf5ec';
const SURFACE = '#fffdf8';
const BORDER = '#e3d8c6';
const INK = '#2f2a24';
const INK_SOFT = '#6b6154';
const INK_MUTED = '#8c7f6c';

/** The smallest a tap target may be. Below this, a shaky hand misses. */
const TOUCH_TARGET = 48;

export const theme = createTheme({
  palette: {
    primary: { main: '#a84a26', dark: '#7d3417', light: '#c46a44', contrastText: '#fffdf8' },
    secondary: { main: '#4a5d3a', dark: '#35432a', light: '#6c7d5b', contrastText: '#fffdf8' },
    error: { main: '#a3341c', light: '#f9e9e2', dark: '#7d2413', contrastText: '#fffdf8' },
    warning: { main: '#a45d10', light: '#fdf3e0', dark: '#7a4a06', contrastText: '#fffdf8' },
    success: { main: '#4a5d3a', light: '#eef1e6', dark: '#35432a', contrastText: '#fffdf8' },
    info: { main: '#2e4a5f', light: '#e6edf2', dark: '#1f3442', contrastText: '#fffdf8' },
    background: { default: PAPER, paper: SURFACE },
    text: { primary: INK, secondary: INK_SOFT, disabled: INK_MUTED },
    divider: BORDER,
    action: {
      hover: 'rgba(168, 74, 38, 0.06)',
      selected: 'rgba(168, 74, 38, 0.10)',
      disabledBackground: '#e0d6c4',
      disabled: '#a2977f',
    },

    /**
     * The neutral tints the design leans on. They are not MUI palette slots,
     * so they live here rather than being re-typed as hex in twenty files.
     */
    surface: {
      paper: PAPER,
      raised: SURFACE,
      chip: '#f0ece0',
      border: BORDER,
      borderStrong: '#d8cbb5',
      rule: '#efe6d6',
    },

    /**
     * One pair per kind of post. The colour is what makes the feed scannable
     * — a lost cat and a recommendation are not the same errand — so each
     * type owns a tint and an ink that reads on it at 4.5:1.
     *
     * These are deliberately *not* MUI colour names. Mapping a post type onto
     * `color="primary"` made a question chip wear the colour of the page's
     * primary action, which taught people to click it.
     */
    postTypes: {
      ANNOUNCEMENT: { bg: '#e6edf2', ink: '#2e4a5f' },
      QUESTION: { bg: '#eae6ef', ink: '#4e3d5c' },
      GOOD_DEAL: { bg: '#eef1e6', ink: '#3c4d2e' },
      LOST_AND_FOUND: { bg: '#fdf3e0', ink: '#7a4a06' },
      RECOMMENDATION: { bg: '#f9e9e2', ink: '#8f3c1c' },
    },

    /** Review stars, and nothing else. */
    rating: '#c07a1e',
  },

  shape: { borderRadius: 14 },

  /**
   * No shadows. The design separates surfaces with a hairline border on a
   * warm ground, which survives being printed, screenshotted and looked at
   * on a cheap screen in a bright kitchen.
   */
  shadows: Array(25).fill('none'),

  typography: {
    fontFamily: BODY_FONT,

    h1: { fontFamily: HEADING_FONT, fontSize: '2.5rem', fontWeight: 500, lineHeight: 1.12, letterSpacing: '-0.02em', color: INK },
    h2: { fontFamily: HEADING_FONT, fontSize: '1.75rem', fontWeight: 600, lineHeight: 1.2, color: INK },
    h3: { fontFamily: HEADING_FONT, fontSize: '1.5rem', fontWeight: 600, lineHeight: 1.2, color: INK },
    h4: { fontFamily: HEADING_FONT, fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.2, color: INK },
    h5: { fontFamily: HEADING_FONT, fontSize: '1.25rem', fontWeight: 600, lineHeight: 1.25, color: INK },
    h6: { fontFamily: HEADING_FONT, fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.25, color: INK },

    // 16.5px / 15px. The floor is 15px: below that the audience stops reading
    // and starts guessing.
    body1: { fontSize: '1.03125rem', lineHeight: 1.6, color: INK },
    body2: { fontSize: '0.9375rem', lineHeight: 1.55, color: INK_SOFT },
    subtitle1: { fontSize: '1.09375rem', lineHeight: 1.5, color: INK_SOFT },
    subtitle2: { fontSize: '1rem', fontWeight: 700, lineHeight: 1.5, color: '#564f44' },
    caption: { fontSize: '0.9375rem', lineHeight: 1.45, color: INK_MUTED },
    button: { fontSize: '1.03125rem', fontWeight: 700, textTransform: 'none' },
    overline: { fontSize: '0.875rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: INK_MUTED },
  },

  components: {
    /**
     * `subtitle1` and `subtitle2` map to <h6> by default, which turns a lede
     * sentence into a heading: a screen reader announces it in the outline
     * and the page grows headings nobody wrote.
     */
    MuiTypography: {
      defaultProps: { variantMapping: { subtitle1: 'p', subtitle2: 'p' } },
    },

    /**
     * The placeholder shapes shown while a block loads.
     *
     * Warm grey rather than MUI's cool default, and no pulse by default: a
     * page of throbbing rectangles is its own kind of agitation. The wave is
     * opted into where the wait is genuinely long.
     */
    MuiSkeleton: {
      defaultProps: { animation: 'wave' },
      styleOverrides: {
        root: { backgroundColor: '#efe6d6', borderRadius: 8 },
        text: { borderRadius: 6, transform: 'none' },
        rectangular: { borderRadius: 12 },
        circular: { borderRadius: 999 },
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        /**
         * The date and time fields are real browser inputs, which is the right
         * choice for this audience: it hands them the picker their phone or
         * computer already taught them.
         *
         * The cost is that the calendar Chrome opens, and the highlight on the
         * segment being edited, are painted by the browser and stay its own
         * blue. Measured, not assumed: `::-webkit-datetime-edit-*-field:focus`
         * and a page-wide `::selection` both reach the stylesheet and neither
         * is honoured — the highlight samples rgb(179, 214, 251) either way.
         * Those attempts were removed rather than left in looking useful.
         *
         * `accent-color` is kept for the native controls that do obey it
         * (checkbox, radio, range) should any appear; MUI draws its own today,
         * so it changes nothing yet. `color-scheme` is the one that earns its
         * place: it keeps those browser popups light, so they cannot come back
         * dark on a machine set to dark mode while the page is cream.
         */
        ':root': {
          accentColor: '#a84a26',
          colorScheme: 'light',
        },
        body: { backgroundColor: PAPER, WebkitFontSmoothing: 'antialiased' },

        // Selecting text anywhere on the page: this one the browser does
        // honour, so a selected sentence comes out warm instead of blue.
        '::selection': {
          backgroundColor: '#f4e2d8',
          color: INK,
        },

        // The little calendar and clock buttons inside those fields are drawn
        // in the page, so they can at least be made big enough to hit.
        '::-webkit-calendar-picker-indicator': {
          cursor: 'pointer',
          padding: 6,
          marginRight: -2,
          borderRadius: 8,
          opacity: 0.75,
        },
        '::-webkit-calendar-picker-indicator:hover': {
          opacity: 1,
          backgroundColor: 'rgba(168, 74, 38, 0.10)',
        },
      },
    },

    /**
     * Buttons are pills, filled, and never smaller than a fingertip.
     *
     * `contained` is the only shape that reads as pressable to someone who
     * has not learned that a thin outline means "button" — so the outlined
     * variant keeps a solid background and a real border rather than being a
     * word floating on the page.
     */
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
          textTransform: 'none',
          paddingLeft: 26,
          paddingRight: 26,
          gap: 10,
        },
        sizeSmall: { minHeight: 44, fontSize: '0.9375rem', paddingLeft: 18, paddingRight: 18 },
        sizeMedium: { minHeight: TOUCH_TARGET, fontSize: '1.03125rem' },
        sizeLarge: { minHeight: 56, fontSize: '1.09375rem' },
        outlined: {
          backgroundColor: SURFACE,
          borderWidth: '1.5px',
          borderColor: '#d8cbb5',
          color: INK,
          '&:hover': { borderWidth: '1.5px', borderColor: INK_MUTED, backgroundColor: SURFACE },
        },
        text: { textDecoration: 'underline', textUnderlineOffset: '3px' },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: { color: INK_SOFT, '&:hover': { backgroundColor: 'rgba(168, 74, 38, 0.08)' } },
        sizeSmall: { width: 40, height: 40 },
        sizeMedium: { width: TOUCH_TARGET, height: TOUCH_TARGET },
      },
    },

    MuiTextField: { defaultProps: { variant: 'outlined' } },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          backgroundColor: SURFACE,
          '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1.5px', borderColor: '#d8cbb5' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: INK_MUTED },
          // The focus ring is a halo, not just a colour change: colour alone
          // is invisible to a part of the audience.
          '&.Mui-focused': { boxShadow: '0 0 0 4px #f4e2d8' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: '2px' },
        },
        input: { fontSize: '1.0625rem', paddingTop: 16, paddingBottom: 16 },
        inputSizeSmall: { paddingTop: 12, paddingBottom: 12 },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: '1.0625rem', color: INK_SOFT, '&.Mui-focused': { color: '#7d3417' } },
      },
    },

    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: '0.9375rem', marginLeft: 2, color: INK_MUTED } },
    },

    MuiMenuItem: {
      styleOverrides: { root: { fontSize: '1.03125rem', minHeight: TOUCH_TARGET, paddingTop: 10, paddingBottom: 10 } },
    },

    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          borderRadius: 18,
          borderColor: BORDER,
          backgroundColor: SURFACE,
          backgroundImage: 'none',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: BORDER, borderRadius: 18, backgroundColor: SURFACE },
      },
    },

    /** A chip labels something. It is never the way an action is offered. */
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 999, fontSize: '0.90625rem', fontWeight: 600, height: 32 },
        label: { paddingLeft: 13, paddingRight: 13 },
        sizeSmall: { height: 30, fontSize: '0.875rem' },
        filled: { backgroundColor: '#f0ece0', color: '#564f44' },
        outlined: { borderColor: '#d8cbb5', backgroundColor: SURFACE },
      },
    },

    /**
     * An alert always carries a sentence. The colour narrows down what kind
     * of news it is; it never *is* the news.
     */
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 14, fontSize: '0.96875rem', border: '1px solid', alignItems: 'flex-start' },
        standardInfo: { backgroundColor: '#e6edf2', borderColor: '#c3d4e0', color: '#24404f' },
        standardSuccess: { backgroundColor: '#eef1e6', borderColor: '#cfdcbc', color: '#35432a' },
        standardWarning: { backgroundColor: '#fdf3e0', borderColor: '#ecd8ac', color: '#6b4000' },
        standardError: { backgroundColor: '#f9e9e2', borderColor: '#e8c3b4', color: '#8f3c1c' },
        message: { paddingTop: 7 },
      },
    },

    MuiAlertTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: '1.03125rem' } } },

    MuiAppBar: {
      defaultProps: { elevation: 0, color: 'transparent' },
      styleOverrides: {
        root: { backgroundColor: SURFACE, borderBottom: `1px solid ${BORDER}`, color: INK },
      },
    },

    MuiDialog: { styleOverrides: { paper: { borderRadius: 22, border: `1px solid ${BORDER}` } } },
    MuiDialogTitle: { styleOverrides: { root: { fontFamily: HEADING_FONT, fontSize: '1.625rem', fontWeight: 600 } } },
    // The buttons sit at the content's own inset rather than MUI's tight 8px,
    // so every dialog ends the same way without each one restating it.
    MuiDialogActions: { styleOverrides: { root: { padding: '8px 24px 24px' } } },

    MuiPagination: { defaultProps: { shape: 'rounded', color: 'primary' } },
    MuiPaginationItem: {
      styleOverrides: {
        root: { borderRadius: 999, minWidth: TOUCH_TARGET, height: TOUCH_TARGET, fontSize: '1.03125rem', fontWeight: 600 },
      },
    },

    MuiDivider: { styleOverrides: { root: { borderColor: '#efe6d6' } } },
    MuiAvatar: { styleOverrides: { root: { backgroundColor: '#4a5d3a', fontWeight: 700, fontSize: '1.03125rem' } } },
    MuiRating: { styleOverrides: { iconFilled: { color: '#c07a1e' }, iconEmpty: { color: '#ded5c3' } } },
    MuiTooltip: { styleOverrides: { tooltip: { fontSize: '0.875rem', backgroundColor: INK, borderRadius: 10 } } },
    MuiLink: { defaultProps: { underline: 'always' }, styleOverrides: { root: { textUnderlineOffset: '3px', fontWeight: 600 } } },
  },
});
